import { cn } from "@/lib/utils";
import { getTypeConfig } from "@/constants/ui-mapping";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Document = Tables<"documents">;

interface SearchResultCardProps {
  doc: Document & { matchScore?: number; matchReason?: string };
  index: number;
  onClick: () => void;
  isSelected?: boolean;
  searchQuery?: string;
}

// Highlight matching text in a string
function highlightText(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;
  
  const words = query.toLowerCase().split(/\s+/).filter(w => w.length >= 2);
  if (words.length === 0) return text;
  
  // Create regex pattern for all search words
  const pattern = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(pattern);
  
  return parts.map((part, i) => {
    const isMatch = words.some(w => part.toLowerCase() === w);
    return isMatch ? (
      <mark key={i} className="bg-primary/20 text-foreground rounded-sm px-0.5">
        {part}
      </mark>
    ) : part;
  });
}

export function SearchResultCard({ doc, onClick, isSelected, searchQuery }: SearchResultCardProps) {
  const typeConfig = getTypeConfig(doc.doc_type);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Get thumbnail URL if available
  const thumbnailUrl = doc.thumbnail_path
    ? supabase.storage.from("thumbnails").getPublicUrl(doc.thumbnail_path).data.publicUrl
    : null;

  return (
    <div
      onClick={onClick}
      className={cn(
        "py-3 px-4 cursor-pointer transition-colors border-b border-border last:border-b-0",
        "hover:bg-muted/50",
        isSelected && "bg-primary/5"
      )}
    >
      <div className="flex items-center gap-3">
        {/* Thumbnail or emoji fallback - 48x60px for better recognition */}
        {thumbnailUrl ? (
          <div className="w-12 h-[60px] shrink-0 rounded-md overflow-hidden bg-muted border border-border">
            <img
              src={thumbnailUrl}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="w-12 h-[60px] shrink-0 rounded-md bg-muted flex items-center justify-center text-xl border border-border">
            {typeConfig.emoji}
          </div>
        )}
        
        <div className="min-w-0 flex-1">
          <h4 className="font-medium text-sm text-foreground truncate">
            {highlightText(doc.title, searchQuery || "")}
          </h4>
          <p className="text-xs text-muted-foreground truncate">
            {typeConfig.label} • {formatDate(doc.created_at)}
          </p>
        </div>
      </div>
    </div>
  );
}